import * as React from 'react';

import { EnrollmentForm } from '@/features/user-notifications/components/enrollments/enrollment-form';
import { SuccessView } from '@/features/user-notifications/components/enrollments/success-view';
import {
  AVAILABLE_NAPLAN_GRADES,
  NAPLAN_PRODUCT_ID,
  NAPLAN_GRADES,
} from '@/features/user-notifications/utils/constants';
import { useAnalytics } from '@/lib/analytics/hooks/use-analytics';
import { useIsSmallScreen } from '@/lib/hooks';

import type { NaplanBatch } from '../../api/get-batches';
import { useBatches, useEnrollToNaplan } from '../../api/queries';

interface NaplanDrawerContentProps {
  onClose: () => void;
}

const NAPLAN_DESCRIPTION =
  'Enjoy free access to a tailored course with recorded videos and weekly practice sessions, excel in the numeracy section of NAPLAN!';

export const NaplanDrawerContent: React.FC<NaplanDrawerContentProps> = ({
  onClose,
}) => {
  const [selectedGrade, setSelectedGrade] = React.useState<string>('');
  const [selectedBatch, setSelectedBatch] = React.useState<NaplanBatch | null>(
    null
  );
  const [showSuccess, setShowSuccess] = React.useState(false);
  const isMobile = useIsSmallScreen();

  const { enroll: enrollToNaplan, isEnrolling } = useEnrollToNaplan();
  const { trackEvent } = useAnalytics();

  // Fetch batches when grade is selected
  const {
    data: batchesData,
    isLoading: isLoadingBatches,
    isError: isBatchesError,
  } = useBatches({
    grade: selectedGrade
      ? (NAPLAN_GRADES[selectedGrade] as string | undefined)
      : undefined,
    product_id: NAPLAN_PRODUCT_ID,
  });

  // Reset batch selection when grade changes
  React.useEffect(() => {
    setSelectedBatch(null);
  }, [selectedGrade]);

  const handleBatchSelect = React.useCallback(
    (batch: NaplanBatch | null) => {
      setSelectedBatch(batch);

      // Analytics tracking for batch selection
      if (batch) {
        trackEvent('NaplanPopupClickedSelectBatch', 1, {
          clickType: 'cta',
          clickCTA: 'Select Batch',
          clickSection: 'Naplan Section',
          batch_id: batch.batch_id,
        });
      }
    },
    [trackEvent]
  );

  const handleEnroll = React.useCallback(() => {
    if (!selectedGrade || !selectedBatch) return;

    // Analytics tracking for enroll click
    trackEvent('NaplanPopupClickedEnrollNow', 1, {
      clickType: 'cta',
      clickCTA: 'Enroll now Naplan',
      clickSection: 'Naplan Section',
      grade: selectedGrade,
      batch_id: selectedBatch.batch_id,
    });

    enrollToNaplan(
      {
        grade: selectedGrade,
        batch_id: selectedBatch.batch_id,
        variant_id: selectedBatch.variant_id,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);

          // Close drawer after 3 seconds
          // Query invalidation in the mutation hook will refresh the data
          setTimeout(() => {
            onClose();
          }, 3000);
        },
      }
    );
  }, [selectedGrade, selectedBatch, enrollToNaplan, trackEvent, onClose]);

  if (showSuccess) {
    return (
      <div>
        <SuccessView
          inline={true}
          isMobile={isMobile}
          onClose={onClose}
          title="Enrolled to NAPLAN Successfully!"
        />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <EnrollmentForm
        selectedGrade={selectedGrade}
        setSelectedGrade={setSelectedGrade}
        isPending={isEnrolling}
        handleEnroll={handleEnroll}
        isMobile={isMobile}
        description={NAPLAN_DESCRIPTION}
        availableGrades={AVAILABLE_NAPLAN_GRADES}
        showBatchSelection={true}
        batches={batchesData?.batches ?? []}
        selectedBatch={selectedBatch}
        setSelectedBatch={setSelectedBatch}
        isLoadingBatches={isLoadingBatches}
        onBatchSelect={handleBatchSelect}
      />
      {!!selectedGrade && !isLoadingBatches && (
        <>
          {!!isBatchesError && (
            <div className="text-center text-sm text-red-500 mt-2">
              Failed to load batches. Please try again.
            </div>
          )}
          {!isBatchesError && batchesData?.batches?.length === 0 && (
            <div className="text-center text-sm text-red-500 mt-2">
              No batches available for the selected grade.
            </div>
          )}
        </>
      )}
    </div>
  );
};
